/**
 * useCareerOverview
 *
 * Stale-while-revalidate hook for career overview data.
 *
 * Strategy:
 *  1. Serve from localStorage cache immediately (zero latency, works offline)
 *  2. If cache is older than CACHE_TTL_MS, fetch fresh data from the API in background
 *  3. Update state + cache when the response arrives
 *  4. Fall back to bundled static JSON if API is unreachable
 */

import { useState, useEffect } from 'react';
import { careerOverviewApi, CareerOverview } from './api/careerOverviewApi';
import { getAllNodes } from '../data/career-tree';
import config from '../../config';

// ─── Resolve any node ID to its parent broad ID ───────────────────────────────
function resolveBroadId(pathId: string): string {
  if (pathId.startsWith('broad_')) return pathId;
  const allNodes = getAllNodes();
  const node = allNodes.find((n) => n.nodeId === pathId);
  if (!node) return pathId;
  if (node.level === 'field') return node.parentId ?? pathId;
  if (node.level === 'specialization') {
    const field = allNodes.find((n) => n.nodeId === node.parentId);
    return field?.parentId ?? pathId;
  }
  return pathId;
}

// ─── For field/spec nodes, return the field name to filter overviews by group ──
function resolveGroupFilter(pathId: string): string | null {
  if (pathId.startsWith('broad_')) return null; // broad = no filter, show all
  const allNodes = getAllNodes();
  const node = allNodes.find((n) => n.nodeId === pathId);
  if (!node) return null;
  if (node.level === 'field') return node.name;
  if (node.level === 'specialization') {
    const field = allNodes.find((n) => n.nodeId === node.parentId);
    return field?.name ?? null;
  }
  return null;
}

// ─── Get the node's own display name (for direct careerName matching) ─────────
function resolveNodeName(pathId: string): string | null {
  if (pathId.startsWith('broad_')) return null;
  const allNodes = getAllNodes();
  const node = allNodes.find((n) => n.nodeId === pathId);
  return node?.name ?? null;
}

// ─── Bundled static fallbacks (always available, never stale) ─────────────────
// Imported lazily per path so the whole 1.6MB isn't always in memory.
const STATIC_FALLBACKS: Record<string, () => Promise<{ default: { careers: any[] } }>> = {
  broad_stem_tech:            () => import('../data/career-overviews/technologyCareerOverview.json'),
  broad_business_finance:     () => import('../data/career-overviews/commerce_business_finance_economics_careers_overview.json'),
  broad_humanities_social:    () => import('../data/career-overviews/humanitiesCareersOverview.json'),
  broad_health_medical:       () => import('../data/career-overviews/scienceCareerOverview.json'),
  broad_creative_arts:        () => import('../data/career-overviews/artLIteratureCareersOverview.json'),
  broad_law_governance:       () => import('../data/career-overviews/humanitiesCareersOverview.json'),
  broad_education_training:   () => import('../data/career-overviews/humanitiesCareersOverview.json'),
  broad_agriculture_environment: () => import('../data/career-overviews/scienceCareerOverview.json'),
};

// ─── Node-level overrides for nodes whose data lives in a different broad's file
// Some field nodes sit under broad_humanities_social in the tree but their
// overview data is in the creative arts JSON. Also corrects group name mismatches.
const STATIC_LOOKUP_OVERRIDES: Record<string, { broadId: string; group: string }> = {
  field_hum_fine_arts:          { broadId: 'broad_creative_arts', group: 'Fine Arts & Visual Expression' },
  field_hum_literature_writing: { broadId: 'broad_creative_arts', group: 'Literature, Writing & Publishing' },
  field_hum_theater_performing: { broadId: 'broad_creative_arts', group: 'Theater & Performing Arts' },
  field_hum_dance_movement:     { broadId: 'broad_creative_arts', group: 'Dance & Movement Arts' },
  field_hum_design_applied:     { broadId: 'broad_creative_arts', group: 'Design & Applied Arts' },
  field_hum_crafts_heritage:    { broadId: 'broad_creative_arts', group: 'Crafts & Heritage Skills' },
  field_hum_arts_management:    { broadId: 'broad_creative_arts', group: 'Arts Management & Education' },
};

// ─── pathId → DB groups (for API queries) ────────────────────────────────────
const PATH_GROUPS: Record<string, string[]> = {
  broad_stem_tech: [
    'Software Development & Engineering',
    'Artificial Intelligence (AI) & Machine Learning',
    'Data Science & Analytics',
    'Cybersecurity & Risk Management',
    'Cloud, DevOps & Infrastructure',
    'Hardware, Semiconductors & Electronics',
    'Business, Sales & Strategy in Tech',
    'Product, Design & Tech Management',
    'Specialized & Immersive Technologies',
    'Technology Policy, Ethics & Law',
  ],
  broad_business_finance: [
    'Banking & Financial Services',
    'Investment Banking & Capital Markets',
    'Corporate Finance & Management',
    'Accounting & Auditing',
    'Actuarial Science & Risk Management',
    'Insurance & Risk Management',
    'Personal Finance & Retail Banking',
    'Global Trade, Economics & Strategy',
    'Specialized & Emerging Commerce Paths',
    'Quantitative & Technical Business Roles',
  ],
  broad_humanities_social: [
    'Social, Behavioral & Political Sciences',
    'Political Science, Governance & Diplomacy',
    'Media, Journalism & Digital Humanities',
    'Language, Linguistics & Communication',
    'Literature, Writing & Publishing',
    'Social Work, Psychology & Mental Health',
    'Sociology, Anthropology & Cultural Studies',
    'Specialized & Rare Humanitarian Careers',
  ],
  broad_health_medical: [
    'Medical, Clinical & Health Sciences',
    'Life & Biological Sciences',
    'Physical & Chemical Sciences',
    'Earth, Environmental & Space Sciences',
  ],
  broad_creative_arts: [
    'Fine Arts & Visual Expression',
    'Design & Applied Arts',
    'Theater & Performing Arts',
    'Dance & Movement Arts',
    'Arts Management & Education',
    'Crafts & Heritage Skills',
    'Literature, Writing & Publishing',
  ],
  broad_law_governance: [
    'Political Science, Governance & Diplomacy',
    'Law, Justice & Human Rights',
    'Specialized & Rare Humanitarian Careers',
  ],
  broad_education_training: [
    'Mathematics Education and Pedagogy',
    'Academia, Research & Consulting',
  ],
  broad_agriculture_environment: [
    'Agricultural, Food & Veterinary Sciences',
    'Earth, Environmental & Space Sciences',
    'Environmental, Energy & Urban Economics',
  ],
};

// ─── Cache helpers ────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const cacheKey = (pathId: string) => `earlyscape_overviews_${pathId}`;

function readCache(pathId: string): { data: CareerOverview[]; ts: number } | null {
  try {
    const raw = localStorage.getItem(cacheKey(pathId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(pathId: string, data: CareerOverview[]) {
  try {
    localStorage.setItem(cacheKey(pathId), JSON.stringify({ data, ts: Date.now() }));
  } catch { /* localStorage full — ignore */ }
}

function mapStaticToOverview(entry: any): CareerOverview {
  const c = entry.career ?? {};
  return {
    careerName:   entry.careerName ?? '',
    slug:         (entry.careerName ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    group:        entry.group ?? '',
    overview:     c.overview ?? '',
    traits:       c.traits ?? [],
    drawbacks:    c.drawbacks ?? [],
    dayToDay:     c.dayToDay ?? [],
    usefulVideos: c.usefulVideos ?? [],
    requirements: {
      academic:      c.requirements?.academic ?? [],
      academicNotes: c.requirements?.academicNotes ?? [],
      trainingNotes: c.requirements?.trainingNotes ?? [],
      other:         c.requirements?.other ?? [],
    },
    routes: {
      academic:           c.routes?.academic ?? [],
      vocation:           c.routes?.vocation ?? [],
      workBased_training: c.routes?.workBased_training ?? [],
    },
    resources: {
      keyLinks:           c.resources?.keyLinks ?? [],
      professionalBodies: c.resources?.professionalBodies ?? [],
      generalInformation: c.resources?.generalInformation ?? [],
      caseStudies:        c.resources?.caseStudies ?? [],
      jobs:               c.resources?.jobs ?? [],
    },
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCareerOverview(pathId: string) {
  const isBroadPath = pathId.startsWith('broad_');
  const cached = readCache(pathId);

  const [overviews, setOverviews] = useState<CareerOverview[]>(cached?.data ?? []);
  const [isLoading, setIsLoading] = useState(overviews.length === 0);

  useEffect(() => {
    if (!pathId) return;
    let cancelled = false;

    const broadId = resolveBroadId(pathId);
    const groupFilter = resolveGroupFilter(pathId);
    const nodeName = resolveNodeName(pathId); // exact name for non-broad paths

    async function load() {
      const cached = readCache(pathId);
      const isFresh = cached && Date.now() - cached.ts < CACHE_TTL_MS;

      // Already have fresh cached data — done
      if (isFresh && cached.data.length > 0) {
        setOverviews(cached.data);
        setIsLoading(false);
        return;
      }

      // Check for a node-level override (fixes cross-broad data placement + group name mismatches)
      const nodeOverride = STATIC_LOOKUP_OVERRIDES[pathId];
      // Effective group name: override takes priority over node name (corrects mismatches)
      const effectiveGroup = nodeOverride?.group ?? groupFilter;

      // Try DB first (production/development mode only)
      if (config.appMode !== 'prototype') {
        try {
          let result: CareerOverview[] = [];

          if (effectiveGroup && nodeName) {
            // Non-broad path: fetch by node name first; if override exists, also try override group name
            const single = await careerOverviewApi.getByName(nodeName);
            if (single) {
              result = [single];
            } else if (nodeOverride && nodeOverride.group !== nodeName) {
              // Some DB entries are stored under the corrected group name rather than the node name
              const byGroup = await careerOverviewApi.getByName(nodeOverride.group);
              if (byGroup) result = [byGroup];
            }
          } else {
            // Broad path: fetch all career overviews for this broad's groups
            const groups = PATH_GROUPS[broadId] ?? [];
            result = (await Promise.all(groups.map(g => careerOverviewApi.getByGroup(g)))).flat();
          }

          if (!cancelled && result.length > 0) {
            setOverviews(result);
            writeCache(pathId, result);
            setIsLoading(false);
            return;
          }
        } catch { /* fall through to static */ }
      }

      // Fall back to bundled static JSON
      // Use override broadId if present (node's data lives in a different broad's file)
      const staticBroadId = nodeOverride?.broadId ?? broadId;
      const loader = STATIC_FALLBACKS[staticBroadId];
      if (loader) {
        try {
          const mod = await loader();
          const allData: CareerOverview[] = (mod.default?.careers ?? []).map(mapStaticToOverview);

          let filtered: CareerOverview[];
          if (!effectiveGroup) {
            // Broad path: show everything
            filtered = allData;
          } else {
            // Non-broad: try exact careerName match first (field/spec has its own entry)
            const nameMatch = nodeName ? allData.filter(o => o.careerName === nodeName) : [];
            // Fall back to group filter (uses corrected group name from override if applicable)
            filtered = nameMatch.length > 0 ? nameMatch : allData.filter(o => o.group === effectiveGroup);
          }

          if (!cancelled) {
            setOverviews(filtered);
            setIsLoading(false);
          }
        } catch { /* no data */ }
      } else {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [pathId]);

  return { overviews, isLoading, isBroadPath };
}
